// @ts-check
// Free training (#146): start the clock and go, no plan.
//
// It runs as a count-up session in the ordinary Coach engine, so heart rate,
// equipment and the Khronos Timer work as they do for any session. What's
// different is the record: the plan was only a stopwatch, so it's saved like a
// logged session instead — the type you pick at the end as `workout_type`,
// and what you logged as you went (#157) as the plan tree — which is the
// shape the history, the AI's training history and the badges already read.

import { activityLabel } from "./loggedSession";
import { guessActivityType, logToPlan } from "./freeTrainingLog";

export const FREE_TRAINING_MODE = "free";
export const FREE_TRAINING_LABEL = "Free training";

/** @param {any} plan */
export const isFreeTraining = (plan) => plan?.freeTraining === true;

/**
 * The parts of the saved session that free training replaces.
 *
 * `plan` is the log as edited at Finish; without one (saved from the Stop
 * dialog) the log is converted as it stands. `activityType` left undefined is
 * guessed from the log; null means the user cleared it. An empty log saves
 * with no plan tree.
 *
 * @param {{ activityType?: string|null, log?: any[], plan?: any }} input
 */
export const freeTrainingRecord = ({ activityType, log = [], plan } = {}) => {
  const type = activityType === undefined ? guessActivityType(log) : activityType;
  const workoutType = type ? activityLabel(type) : FREE_TRAINING_LABEL;
  const tree = plan === undefined ? logToPlan(log, workoutType) : plan;
  return {
    workout_type: workoutType,
    plan_data: tree?.children?.length ? { ...tree, label: workoutType } : null,
  };
};

/**
 * Laps as splits: how long each took, and the clock when it ended. Free
 * training saved laps before the log replaced them (#146 → #157); History
 * still shows them for those sessions.
 * @param {number[]} laps
 */
export const lapSplits = (laps) =>
  (Array.isArray(laps) ? laps : []).map((at, i, all) => ({
    n: i + 1,
    at,
    split: at - (i > 0 ? all[i - 1] : 0),
  }));

/** Seconds as m:ss, or h:mm:ss past the hour. @param {number} seconds */
export const formatLapTime = (seconds) => {
  const total = Math.max(0, Math.round(seconds || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
};

/**
 * Running heart-rate figures for the stats strip, one sample a second.
 * @typedef {{ count: number, sum: number, max: number, zoneSeconds: number[] }} HrStats
 */
/** @returns {HrStats} */
export const emptyHrStats = () => ({ count: 0, sum: 0, max: 0, zoneSeconds: [0, 0, 0, 0, 0, 0] });

/**
 * @param {HrStats} stats
 * @param {number|null} bpm
 * @param {number|null} zone 0–5, or null when there are no zones to use
 * @returns {HrStats}
 */
export const addHrSample = (stats, bpm, zone = null) => {
  if (!Number.isFinite(bpm) || /** @type {number} */ (bpm) <= 0) return stats;
  const value = /** @type {number} */ (bpm);
  const zoneSeconds = [...stats.zoneSeconds];
  if (zone != null && zone >= 0 && zone <= 5) zoneSeconds[zone] += 1;
  return {
    count: stats.count + 1,
    sum: stats.sum + value,
    max: Math.max(stats.max, value),
    zoneSeconds,
  };
};

/** @param {HrStats} stats */
export const averageHr = (stats) => (stats.count ? Math.round(stats.sum / stats.count) : null);
